import React, { useState } from 'react';
import { useProperties } from '../hooks/useProperties';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, MapPin, Home, Users, Calendar, Key, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Property } from '../types';
import { NumberUtils } from '@cleanpro/common-ui';

export const Properties: React.FC = () => {
  const { properties, loading, error } = useProperties();
  const { customers } = useCustomers();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [propertyFilter, setPropertyFilter] = useState('all');

  const ITEMS_PER_PAGE = 25;

  // Get customer names for properties
  const propertiesWithCustomers = properties.map(property => {
    const customer = customers.find(c => c.id === property.customerId);
    return {
      ...property,
      customerName: customer ? customer.name : 'Unknown Customer'
    };
  });


  // Property status filters based on workflow insights
  const PROPERTY_FILTERS = [
    { 
      value: 'all', 
      label: 'All properties', 
      count: properties.length 
    },
    { 
      value: 'active', 
      label: 'Active', 
      count: properties.filter(p => p.isActive).length 
    },
    { 
      value: 'residential', 
      label: 'Residential', 
      count: properties.filter(p => p.propertyType === 'house' || p.propertyType === 'apartment').length 
    },
    { 
      value: 'commercial', 
      label: 'Commercial', 
      count: properties.filter(p => p.propertyType === 'office').length 
    },
  ];

  const filteredProperties = propertiesWithCustomers.filter(property =>
    property.addressLine1.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (property.customerName && property.customerName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Calculate pagination
  const totalItems = filteredProperties.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentProperties = filteredProperties.slice(startIndex, endIndex);

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getPropertyTypeIcon = (type: string) => {
    switch (type) {
      case 'house': return <Home className="h-5 w-5" />;
      case 'apartment': return <Home className="h-5 w-5" />;
      case 'condo': return <Home className="h-5 w-5" />;
      case 'townhouse': return <Home className="h-5 w-5" />;
      case 'office': return <Users className="h-5 w-5" />;
      default: return <Home className="h-5 w-5" />;
    }
  };

  const formatAddress = (property: Property) => {
    return `${property.addressLine1}${property.addressLine2 ? ', ' + property.addressLine2 : ''}, ${property.city}, ${property.state} ${property.postalCode}`;
  };

  if (loading) return <div>Loading properties...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Properties</h1>
        <button
          onClick={() => navigate('/properties/new')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Property</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search properties by address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Property Type Filters */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Filter by Property Type:</p>
            <div className="flex flex-wrap gap-2">
              {PROPERTY_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setPropertyFilter(filter.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    propertyFilter === filter.value
                      ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

        {/* Properties Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Special Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentProperties.map((property) => (
                  <tr key={property.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/properties/${property.id}`)}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-indigo-600">
                            {getPropertyTypeIcon(property.propertyType)}
                          </div>
                          <p className="text-sm font-medium text-gray-900">{property.addressLine1}</p>
                        </div>
                        <p className="text-sm text-gray-500 capitalize">{property.propertyType}</p>
                        <p className="text-xs text-gray-400">#{property.id}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {property.customerName && (
                          <p className="text-sm font-medium text-gray-900">{property.customerName}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-start text-sm text-gray-900">
                        <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="truncate max-w-48">{formatAddress(property)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(property.bedrooms || property.bathrooms || property.squareFootage) && (
                          <div className="space-y-1">
                            {property.bedrooms && property.bedrooms > 0 && (
                              <div>{property.bedrooms} bed</div>
                            )}
                            {property.bathrooms && property.bathrooms > 0 && (
                              <div>{property.bathrooms} bath</div>
                            )}
                            {property.squareFootage && (
                              <div className="text-xs text-gray-500">{NumberUtils.formatNumber(property.squareFootage, 0)} sq ft</div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(property.preferredCleaningDay || property.requestedTimeSlot) && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-indigo-600" />
                            <div>
                              {property.preferredCleaningDay && (
                                <div className="capitalize">{property.preferredCleaningDay}s</div>
                              )}
                              {property.requestedTimeSlot && (
                                <div className="text-xs text-gray-500 capitalize">{property.requestedTimeSlot}</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="space-y-1">
                          {property.hasPets && (
                            <div className="flex items-center gap-1 text-amber-600">
                              <Heart className="h-3 w-3" />
                              <span className="text-xs">Pets</span>
                            </div>
                          )}
                          {property.accessInstructions && (
                            <div className="flex items-center gap-1 text-indigo-600">
                              <Key className="h-3 w-3" />
                              <span className="text-xs">Access Info</span>
                            </div>
                          )}
                          {property.specialInstructions && (
                            <div className="text-xs text-gray-500">Special Instructions</div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} properties
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                      // Show first page, current page and surrounding pages, and last page
                      const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
                      const showEllipsis = (page === 2 && currentPage > 4) || (page === totalPages - 1 && currentPage < totalPages - 3);
                      
                      if (!showPage && !showEllipsis) return null;
                      
                      if (showEllipsis) {
                        return (
                          <span key={`ellipsis-${page}`} className="px-2 py-1 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                            currentPage === page
                              ? 'bg-indigo-600 border-indigo-600 text-white'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {filteredProperties.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No properties found matching your search.</p>
          </div>
        )}
      </div>
    );
};
